import React, { useEffect, useState, useContext } from 'react';
import axios from '@js/modules/axios/index';
import { useForm, Controller } from 'react-hook-form';
import { ErrorMessage } from '@hookform/error-message';

import { InvestorRegistrationContainer } from '@js/components/common/investorRegistrations/InvestorRegistrationContainer';
import { CorporateIdentificationValidation } from '@js/formSettings/validations';
import { IdentificationFormNote } from '@js/components/corporateRegistrations/Identification/IdentificationFormNote';
import { IdentificationDocumentNote } from '@js/components/corporateRegistrations/Identification/IdentificationDocumentNote';
import { IdentificationImageNote } from '@js/components/corporateRegistrations/Identification/IdentificationImageNote';
import { usePath } from '@js/hooks/usePath';
import { Context } from '@js/routes';
import {
  category1,
  category2,
  firstDocumentOptions,
  secondDocumentOptions
} from '@js/components/corporateRegistrations/Identification/constants';
import type {
  DefaultValues,
  IdentificationFormData,
  Category1,
  Category2,
  IdentificationReceivedData
} from '@js/components/corporateRegistrations/Identification/types';


export const CorporateIdentification = () => {
  const { setIsLoading, isEdit, setIsEdit, setIsSubmitting } = useContext(Context);
  const [defaultValues, setDefaultValues] = useState<DefaultValues>({
    category1: category1[0],
    image1: [],
    category2: category2[0],
    image2: [],
  });
  const [isDefault, setIsDefault] = useState(false);
  const [uploadedFileErrorText, setUploadedFileErrorText] = useState('');

  const { register, handleSubmit, formState: { errors }, watch, setValue, control, clearErrors } = useForm<IdentificationFormData>({
    shouldUnregister: false
  });
  const watchAll = watch();
  const watchFirstDocumentKind = watchAll.first_document_kind || category1[0] as Category1;
  const watchSecondDocumentKind = watchAll.second_document_kind|| category2[0] as Category2;
  const { currentStep, pushNext } = usePath(isEdit);
  const onSubmit = (data: IdentificationFormData) => {
    // ２回目以降の登録でフォームの値を全くいじっていない状態で次のステップに進む場合
    // どれか一つでもフォームの値を変更したら画像を全て登録しないとバリデーションで弾かれる
    // つまり、data.first_document_frontがundefined時は常に2回目の登録かつ値の変更なしが保証される
    if(!data.first_document_front) {
      return setTimeout(pushNext, 1000);
    } else {
      setIsSubmitting(true);
      const submitData = new FormData();
      submitData.append('category1', data.first_document_kind);
      submitData.append('category1_image1', data.first_document_front[0]);
      submitData.append('category1_image2', data.first_document_back ? data.first_document_back[0] : '');
      submitData.append('category2', data.second_document_kind);
      submitData.append('category2_image1', data.second_document_front[0]);
      submitData.append('category2_image2', data.second_document_back ? data.second_document_back[0]: '');
      axios.post('/api/v1/customer/corporate_registration/identification', submitData)
        .then(() => {
          setTimeout(pushNext, 1000);
        })
        .catch((error) => {
          setIsSubmitting(false);
          const errorText = error.response.data.errors[0];
          if (errorText.includes('Identification documents image')) {
            const replacedText = errorText.replace('Identification documents image', 'アップロードしたファイル');
            setUploadedFileErrorText(replacedText);
          }
        });
    }
  };

  useEffect(() => {
    setIsLoading(true);
    axios.get('/api/v1/customer/corporate_registration/identification')
      .then(response => {
        const data = response.data as IdentificationReceivedData<Category1 | null>;
        setIsEdit(!data['registration_in_progress?']);
        if(!data['registration_in_progress?']) {
          setValue('first_document_kind', category1[0]);
          setValue('second_document_kind', category2[0]);
          return;
        }
        if(data.default_identification1.category) {
          const image1_front = data.default_identification1.image1.metadata.filename;
          const image1_back = data.default_identification1.image2 ? data.default_identification1.image2.metadata.filename : '';
          const image2_front = data.default_identification2.image1.metadata.filename;
          const image2_back = data.default_identification2.image2 ? data.default_identification2.image2.metadata.filename : '';
          setDefaultValues({
            category1: data.default_identification1.category,
            image1: [image1_front, image1_back],
            category2: data.default_identification2.category,
            image2: [image2_front, image2_back],
          });
          setValue('first_document_kind', data.default_identification1.category  || category1[0]);
          setValue('second_document_kind', data.default_identification2.category || category2[0]);
          setIsDefault(true);
        } else {
          setValue('first_document_kind', category1[0]);
          setValue('second_document_kind', category2[0]);
        }
      }).finally(() => setIsLoading(false));
    }, []); // eslint-disable-line

  return (
    <InvestorRegistrationContainer formId={currentStep.name} isEdit={isEdit} >
      <div className="mt40"><IdentificationFormNote /></div>
      <form onSubmit={handleSubmit(onSubmit)} className="form-wrap" id={currentStep.name} >
        <fieldset>
          <legend className="form-label">
            <span className="form-label__required">必須</span>１つめの本人確認書類
          </legend>
          <div className="form-input__wrap">
            {Object.entries(firstDocumentOptions).map(([optionKey, option], index) =>
              <div key={option.name} className="form-column">
                <div className='form-radio'>
                  <label className="form-radio__label mr32" htmlFor={`${optionKey}_${index}_1st`}>
                    <input
                      {...register('first_document_kind', {
                        onChange: e => setIsDefault(e.target.value === defaultValues.category1 && watchSecondDocumentKind === defaultValues.category2)
                      })}
                      type="radio"
                      id={`${optionKey}_${index}_1st`}
                      className={`form-radio__input ${errors.first_document_kind}`}
                      value={category1[index]}
                    />
                    <span className="form-radio__text">{ option.name }</span>
                  </label>
                </div>
              </div>
            )}
          </div>
        </fieldset>
        <fieldset className="form-input__wrap">
          {
            errors.first_document_front ?  (
              <ErrorMessage
                errors={errors}
                name="first_document_front"
                render={({ message }) =>
                  <span className="form-input__error form-file-block__error">{message}</span>}
              />
            ) : (
              <ErrorMessage
                errors={errors}
                name="first_document_back"
                render={({ message }) =>
                  <span className="form-input__error form-file-block__error">{message}</span>}
              />
            )
          }
          <div className="bg--gray form-file-block__wrap tab-contents is-active" data-tab="groupA">
            <div className="form-file-block">
              <legend className="form-file-block__heading">
                <span className="form-label__required">必須</span>1つめの本人確認書類のアップロード
              </legend>
              <IdentificationDocumentNote />
              <div className="form-file-block__button">
                {firstDocumentOptions[watchFirstDocumentKind].label.map((label, index) =>
                  <Controller
                    key={label}
                    control={control}
                    name={!index ? 'first_document_front' : 'first_document_back'}
                    rules={{
                      validate: {
                        hasUploaded: (value) => {
                          const neededFileNum = firstDocumentOptions[watchFirstDocumentKind].label.length;
                          const isUploaded =
                            neededFileNum < 2
                              ? Boolean(value && value[0])
                              : index === 0
                                ? Boolean(watchAll.first_document_back)
                                : Boolean(watchAll.first_document_front);
                          isUploaded && clearErrors(['first_document_front', 'first_document_back']);
                          return isUploaded || isDefault || '本人確認書類の画像が足りません';
                        },
                        isOriginal: (value) => {
                          if(!(watchAll.first_document_front && watchAll.first_document_back)) return; //false;
                          const isSingle = firstDocumentOptions[watchFirstDocumentKind].label.length < 2;
                          const isDeferentName = index === 0
                            ? Boolean((value && value[0].name) !== watchAll.first_document_back[0].name)
                            : Boolean(watchAll.first_document_front[0].name !== (value && value[0].name));
                          return isSingle || isDeferentName || isDefault || '本人確認書類の画像はそれぞれ違うファイル名にしてください。';
                        }
                      }
                    }}
                    render={({field: { onChange, value }}) =>
                      <div className="form-input__wrap">
                        <label className="form-file-block__label" htmlFor={!index ? 'first_document_front' : 'first_document_back'}>{`${index + 1}.${firstDocumentOptions[watchFirstDocumentKind].name}${label}`}</label>
                        <div className="form-file__wrap">
                          <span className="button-short-base--line form-file__button">
                            <input
                              onChange={e => { if (e && e.target && e.target.files && e.target.files.length > 0) onChange(e.target.files);  setIsDefault(false); }}
                              className="form-file form-file--corporate"
                              accept=".jpg, .jpeg, .png"
                              type="file"
                              id={!index ? 'first_document_front' : 'first_document_back'}
                            />
                            ファイルを選択する
                          </span>
                          <div className="form-file__name">{ (value && value[0].name) || (isDefault && defaultValues.image1[index]) || '選択されていません'}</div>
                        </div>
                      </div>
                    }
                  />
                )}
              </div>
            </div>
            <IdentificationImageNote documentKind={watchFirstDocumentKind}/>
          </div>
        </fieldset>
        <fieldset>
          <legend className="form-label">
            <span className="form-label__required">必須</span>2つめの本人確認書類
          </legend>
          <div className="form-file-block__note">
            <p>※1つめと同じ書類は登録することができません</p>
          </div>
          <div className="form-input__wrap">
            {Object.entries({...firstDocumentOptions, ...secondDocumentOptions}).map(([optionKey, option], index) =>
              <div key={option.name} className="form-column">
                <div className='form-radio'>
                  <label className="form-radio__label mr32" htmlFor={`${optionKey}_${index}_2nd`}>
                    <input
                      {...register('second_document_kind', {
                        ...CorporateIdentificationValidation.documentKind(watchFirstDocumentKind, watchSecondDocumentKind),
                        onChange: e => setIsDefault(e.target.value === defaultValues.category2 && watchFirstDocumentKind === defaultValues.category1)
                      })}
                      type="radio"
                      id={`${optionKey}_${index}_2nd`}
                      className={`form-radio__input ${errors.first_document_kind}`}
                      value={category2[index]}
                    />
                    <span className="form-radio__text">{ option.name }</span>
                  </label>
                </div>
              </div>
            )}
          </div>
          <ErrorMessage
            errors={errors}
            name="second_document_kind"
            render={() =>
              <span className="form-input__error">2つめの本人確認書類は1つめとは別の種別を選択してください</span>}
          />
        </fieldset>
        <fieldset className="form-input__wrap">
          <ErrorMessage
            errors={errors}
            name="second_document_front"
            render={({ message }) =>
              <span className="form-input__error form-file-block__error">{message}</span>}
          />
          <div className="bg--gray form-file-block__wrap tab-contents is-active" data-tab="groupA">
            <div className="form-file-block">
              <legend className="form-file-block__heading">
                <span className="form-label__required">必須</span>2つめの本人確認書類のアップロード
              </legend>
              <IdentificationDocumentNote />
              <div className="form-file-block__button">
                {secondDocumentOptions[watchSecondDocumentKind].label.map((label, index) =>
                  <Controller
                    key={label}
                    control={control}
                    name={!index ? 'second_document_front' : 'second_document_back'}
                    rules={{
                      validate: {
                        hasUploaded: (value) => {
                          const neededFileNum = secondDocumentOptions[watchSecondDocumentKind].label.length;
                          const isUploaded =
                            neededFileNum < 2
                              ? Boolean(value && value[0])
                              : index === 0
                                ? Boolean(watchAll.second_document_back)
                                : Boolean(watchAll.second_document_front);
                          isUploaded && clearErrors(['second_document_front', 'second_document_back']);
                          return isUploaded || isDefault || '本人確認書類の画像が足りません';
                        },
                        isOriginal: (value) => {
                          if(!(watchAll.second_document_front && watchAll.second_document_back)) return; //false;
                          const isSingle = secondDocumentOptions[watchSecondDocumentKind].label.length < 2;
                          const isDeferentName = index === 0
                            ? Boolean((value && value[0].name) !== watchAll.second_document_back[0].name)
                            : Boolean(watchAll.second_document_front[0].name !== (value && value[0].name));
                          return isSingle || isDeferentName || isDefault || '本人確認書類の画像はそれぞれ違うファイル名にしてください。';
                        }
                      }
                    }}
                    render={({field: { onChange, value }}) =>
                      <div className="form-input__wrap">
                        <label className="form-file-block__label" htmlFor={!index ? 'second_document_front' : 'second_document_back'}>{`${index + 1}.${secondDocumentOptions[watchSecondDocumentKind].name}${label}`}</label>
                        <div className="form-file__wrap">
                          <span className="button-short-base--line form-file__button">
                            <input
                              onChange={e => { if (e && e.target && e.target.files && e.target.files.length > 0) onChange(e.target.files);  setIsDefault(false); }}
                              className="form-file form-file--corporate"
                              accept=".jpg, .jpeg, .png"
                              type="file"
                              id={!index ? 'second_document_front' : 'second_document_back'}
                            />
                            ファイルを選択する
                          </span>
                          <div className="form-file__name">{ (value && value[0].name) || (isDefault && defaultValues.image2[index]) || '選択されていません'}</div>
                        </div>
                      </div>
                    }
                  />
                )}
              </div>
            </div>
            <IdentificationImageNote documentKind={watchSecondDocumentKind}/>
          </div>
        </fieldset>
        <div className='form-input__error'>{uploadedFileErrorText}</div>
      </form>
    </InvestorRegistrationContainer>
  );
};
